/* eslint-disable react/no-unstable-nested-components */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { TDividendsPaidByYear } from "components/company/v3/brazilian/contents/events/dividends/types";
import classNames from "components/ui/classNames";
import { ScrollArea } from "components/ui/ScrollArea";
import TablePagination from "components/ui/Table/TablePagination";
import numeral from "numeral";
import { FormattedMessage } from "react-intl";

interface AmericanDividendsPaidByYearProps {
  dividendsPaid: TDividendsPaidByYear[];
}

export default function AmericanDividendsPaidByYear({ dividendsPaid }: AmericanDividendsPaidByYearProps) {
  const columnHelper = createColumnHelper<TDividendsPaidByYear>();

  const columns = [
    columnHelper.accessor("year", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Ano" id="wY7G7a" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor("amountPaid", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Valor ($)" id="1Gyye/" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{numeral(info.getValue()).format("0.00").replace(".", ",")}</span>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: dividendsPaid,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 16,
      },
    },
  });

  return (
    <>
      <ScrollArea type="auto" orientation="horizontal" className="relative h-full w-full">
        <div className="pb-4 lg:w-full">
          <div
            className={classNames("relative overflow-auto rounded-xl border border-slate-200", "dark:border-slate-950")}
          >
            <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                  >
                    {headerGroup.headers
                      .filter((header) => !header.column.columnDef.meta?.isHidden)
                      .map((header) => {
                        return (
                          <th
                            key={header.id}
                            className={classNames(
                              "m-0 bg-slate-50 p-0 text-center",
                              header.column.columnDef.meta?.header?.className,
                            )}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        );
                      })}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                      {row
                        .getVisibleCells()
                        .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                        .map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              className={classNames("m-0 p-0 text-center", cell.column.columnDef.meta?.cell?.className)}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </ScrollArea>

      <TablePagination<TDividendsPaidByYear> table={table} />
    </>
  );
}
